import { useState } from "react";
import axios from "axios";
import { Clock, Loader2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { API, CONTACT } from "@/data/site";
import { Overline, Reveal } from "@/components/motion";

const inputCls =
  "w-full border border-white/10 bg-stone-950/70 px-4 py-3 text-sm font-light text-stone-100 placeholder:text-stone-600 outline-none transition-colors duration-300 focus:border-amber-600/60";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Your note is on its way to the tea house. We reply within a day.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Could not send your note. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-t border-white/5 py-28 md:py-36"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <Overline>Contact</Overline>
          <h2 className="mt-5 font-serif text-4xl leading-[1.05] tracking-tight text-stone-100 sm:text-5xl lg:text-6xl">
            Write to the <em className="not-italic text-amber-500">tea house</em>
          </h2>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-stone-400 md:text-lg">
            Wholesale enquiries, gifting hampers, or simply which tea suits your evenings —
            every note is read over a fresh pot.
          </p>

          <div className="mt-12 space-y-6">
            <a
              data-testid="contact-email-link"
              href={`mailto:${CONTACT.email}`}
              className="group flex items-center gap-5"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 text-amber-500 transition-colors duration-300 group-hover:border-amber-600/60">
                <Mail className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.35em] text-stone-500">Email</span>
                <span className="block text-base font-light text-stone-200 transition-colors duration-300 group-hover:text-amber-400">
                  {CONTACT.email}
                </span>
              </span>
            </a>
            <a
              data-testid="contact-phone-link"
              href={`tel:${CONTACT.phoneRaw}`}
              className="group flex items-center gap-5"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 text-amber-500 transition-colors duration-300 group-hover:border-amber-600/60">
                <Phone className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.35em] text-stone-500">Phone</span>
                <span className="block text-base font-light text-stone-200 transition-colors duration-300 group-hover:text-amber-400">
                  {CONTACT.phoneDisplay}
                </span>
              </span>
            </a>
            <div className="flex items-center gap-5">
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 text-amber-500">
                <Clock className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.35em] text-stone-500">Hours</span>
                <span className="block text-base font-light text-stone-200">{CONTACT.hours}</span>
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="border border-white/10 bg-stone-900/40 p-7 md:p-9"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-stone-500">
                  Name
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your full name"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-stone-500">
                  Email
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputCls}
                />
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="contact-phone" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-stone-500">
                Phone <span className="text-stone-600">(optional)</span>
              </label>
              <input
                id="contact-phone"
                data-testid="contact-phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="+91 ..."
                className={inputCls}
              />
            </div>
            <div className="mt-5">
              <label htmlFor="contact-message" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-stone-500">
                Message
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                minLength={5}
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us what you're brewing..."
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="mt-7 inline-flex w-full items-center justify-center gap-3 rounded-full bg-amber-600 px-7 py-4 text-sm font-medium text-stone-950 transition-all duration-300 hover:bg-amber-500 hover:shadow-[0_0_36px_rgba(217,119,6,0.3)] disabled:opacity-60"
            >
              {sending && <Loader2 className="h-4 w-4 animate-spin" />}
              {sending ? "Sending..." : "Send to the tea house"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
